import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Typography,
} from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { IconChevronLeft } from "@tabler/icons-react";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { DatastoreTypeEnum } from "../../../types";
import { ConnectionProps } from "./types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postIntegration } from "@/helpers/apis";
import { toast } from "@altimateai/lego";
import { useNavigate } from "react-router-dom";
import { GET_INTEGRATIONS } from "../../../types";

const connectionSchema = z.object({
  environment: z
    .string({ message: "Environment name is required" })
    .min(3, { message: "Environment name must be at least 3 characters long" }),
});

type ConnectionFormValues = z.infer<typeof connectionSchema>;

const DbtCoreConnection = ({
  typeData,
  handleBack,
}: ConnectionProps): JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(connectionSchema),
    defaultValues: {
      environment: "",
    },
  });

  const { mutate: createIntegration, isPending: isCreating } = useMutation({
    mutationFn: postIntegration,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "dbt Core integration created successfully",
      });
      queryClient.invalidateQueries({ queryKey: [GET_INTEGRATIONS] });
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Unable to create dbt Core integration!",
      });
    },
  });

  const handleSubmit = (values: ConnectionFormValues) => {
    createIntegration({
      name: typeData.name || "",
      integration_type: "dbt_core",
      environment: values.environment,
    });
  };

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DBT_CORE,
    className: "al-w-10 al-h-10 al-mr-3",
  });

  return (
    <Card>
      <CardContent>
        <Stack direction="row" align="center" className="al-mb-6 al-pt-6">
          {icon}
          <div>
            <Typography variant="subheading">dbt Core Connection</Typography>
            <Typography size="sm" className="al-text-muted-foreground">
              Configure your dbt Core integration
            </Typography>
          </div>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="al-space-y-6">
            <FormField
              control={form.control}
              name="environment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., PROD, DEV, STAGING" {...field} />
                  </FormControl>
                  <FormDescription>
                    The environment name for your dbt project (e.g., PROD)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Typography size="sm" className="al-text-muted-foreground al-pt-2">
              After creation, you can upload your dbt artifacts (manifest.json, catalog.json) to
              sync your dbt project metadata.
            </Typography>

            <Stack justify="between" className="al-pt-6">
              <Button type="button" variant="outline" onClick={handleBack}>
                <IconChevronLeft size={16} />
                Back
              </Button>
              <LoadingButton type="submit" loading={isCreating}>
                Create Integration
              </LoadingButton>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DbtCoreConnection;
